'use client'

import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

interface MonthPickerProps {
  value: string // YYYY-MM
  onValueChange?: (value: string) => void
  id?: string
  disabled?: boolean
  className?: string
  placeholder?: string
}

function parseYearMonth(value: string) {
  const [yearStr, monthStr] = value.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const now = new Date()
  return {
    year: year >= 2000 ? year : now.getFullYear(),
    month: month >= 1 && month <= 12 ? month : now.getMonth() + 1,
  }
}

function toYearMonthString(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

function formatDisplay(value: string): string {
  const { year, month } = parseYearMonth(value)
  return `${MONTH_LABELS[month - 1]} ${year}`
}

function MonthPicker({
  value,
  onValueChange,
  id,
  disabled,
  className,
  placeholder = 'Pick a month',
}: MonthPickerProps) {
  const [open, setOpen] = React.useState(false)
  const { year: selectedYear, month: selectedMonth } = parseYearMonth(value)
  const [viewYear, setViewYear] = React.useState(selectedYear)

  // Sync view year when value changes externally
  React.useEffect(() => {
    setViewYear(parseYearMonth(value).year)
  }, [value])

  const handleSelect = (month: number) => {
    onValueChange?.(toYearMonthString(viewYear, month))
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-36 justify-between px-3 font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          {value ? formatDisplay(value) : placeholder}
          <CalendarIcon className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        {/* Year navigation */}
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setViewYear((y) => y - 1)}
            className="rounded p-1 hover:bg-accent"
            aria-label="Previous year"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="font-semibold text-sm">{viewYear}</span>
          <button
            type="button"
            onClick={() => setViewYear((y) => y + 1)}
            className="rounded p-1 hover:bg-accent"
            aria-label="Next year"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
        {/* Month grid */}
        <div className="grid grid-cols-3 gap-1">
          {MONTH_LABELS.map((label, index) => {
            const monthNum = index + 1
            const isSelected =
              viewYear === selectedYear && monthNum === selectedMonth
            return (
              <button
                key={label}
                type="button"
                onClick={() => handleSelect(monthNum)}
                className={cn(
                  'rounded px-2 py-1.5 text-center text-sm hover:bg-accent',
                  isSelected &&
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { MonthPicker }
